<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue";
import { onClickOutside } from "@vueuse/core";
import {
    ChevronDownIcon,
    ChevronUpIcon,
    CheckIcon,
} from "@heroicons/vue/24/outline";
import { useGlobalFilterStore } from "@/stores/globalFilterStore";
import { useUserStore } from "@/stores/userStore";
import { storeToRefs } from "pinia";
import { analytics } from "@/services/segment.ts";
import svgIcon from "@/components/_elements/SvgIcon.vue";

const globalFilterStore = useGlobalFilterStore();
const { isSet, value, availableModalityOptions, allModalities } = storeToRefs(globalFilterStore);

const userStore = useUserStore();
const { loaded } = storeToRefs(userStore);

// Always resolve the trigger label from the full list — the active modality
// may not be in the enrollment-filtered set (e.g. if enrollments haven't
// loaded yet or the user's modality has no courses this session).
const currentOption = computed(
    () => allModalities.value.find((o) => o.value === value.value) ?? null
);

// ── Dropdown ────────────────────────────────────────────────────────────────

function onMenuButtonClick(isCurrentlyOpen: boolean) {
    if (!isCurrentlyOpen) {
        analytics.track("Learn Global Filter Dropdown Opened");
    }
}

async function handleSelect(newValue: string): Promise<void> {
    const from = value.value;
    if (from === newValue) return;

    analytics.track("Learn Global Filter Category Changed", {
        from,
        to: newValue,
    });

    await globalFilterStore.setFilter(newValue);

    if (isCourseContentPage(window.location.pathname)) {
        window.location.replace("/learn/courses");
    }
}

function isCourseContentPage(pathname: string): boolean {
    return pathname.startsWith("/learn/courses/") && pathname !== "/learn/courses/";
}

// ── Coachmark ───────────────────────────────────────────────────────────────

const COACHMARK_KEY = "clover.global-filter-coachmark";

// Module-level state shared across both component instances (desktop TitleBar
// + mobile PrimaryLayout). Using a module ref ensures the coachmark dismisses
// everywhere when one instance receives the action, and prevents analytics
// events from firing twice.
const _coachmarkDismissed = ref(localStorage.getItem(COACHMARK_KEY) === "true");
let _coachmarkShownTracked = false;

const coachmarkVisible = computed(
    () => loaded.value && isSet.value && !_coachmarkDismissed.value
);

watch(coachmarkVisible, (visible) => {
    if (visible && !_coachmarkShownTracked) {
        _coachmarkShownTracked = true;
        analytics.track("Learn Global Filter Coachmark Shown");
    }
});

function dismiss(method: "got_it" | "click_outside" | "navigation"): void {
    if (!coachmarkVisible.value) return;
    _coachmarkDismissed.value = true;
    localStorage.setItem(COACHMARK_KEY, "true");
    analytics.track("Learn Global Filter Coachmark Dismissed", { method });
}

// Click-outside: any click outside the containerRef dismisses the coachmark.
const containerRef = ref<HTMLElement | null>(null);
onClickOutside(containerRef, () => dismiss("click_outside"));

// Navigation: best-effort fire before unload.
function handleBeforeUnload(): void {
    dismiss("navigation");
}

onMounted(() => {
    window.addEventListener("beforeunload", handleBeforeUnload);
});

onBeforeUnmount(() => {
    window.removeEventListener("beforeunload", handleBeforeUnload);
});
</script>

<template>
    <div ref="containerRef" class="relative sm:h-full w-full bg-white sm:bg-transparent flex-col justify-center">
        <!-- Skeleton: reserves space while auth-status is loading -->
        <div
            v-if="!loaded"
            class="flex items-center gap-3 px-5 py-2"
            aria-hidden="true"
        >
            <div class="h-9 w-9 shrink-0 rounded border border-gray-200 bg-gray-100 animate-pulse" />
            <div class="flex flex-col gap-1.5 min-w-0">
                <div class="h-3.5 w-28 rounded bg-gray-200 animate-pulse" />
                <div class="h-2.5 w-40 rounded bg-gray-100 animate-pulse" />
            </div>
            <div class="h-4 w-4 shrink-0 rounded bg-gray-100 animate-pulse" />
        </div>

        <!-- Dropdown trigger + panel (once loaded with modality set) -->
        <Menu v-else-if="isSet" v-slot="{ open }" as="div" class="sm:h-full">
            <MenuButton
                class="flex sm:h-full w-full sm:max-w-[420px] items-center gap-3 px-5 py-2 text-left sm:hover:bg-gray-100 transition-colors border-r border-r-gray-300"
                @click="onMenuButtonClick(open)"
            >
              <svg-icon
                  :icon="currentOption?.icon ?? 'x-ray'"
                  size="xl"
                  type="fill"
                  class="text-gray-900 shrink-0"
              ></svg-icon>
                <div class="flex flex-col min-w-0 flex-1">
                    <span class="font-bold text-gray-900 text-sm leading-tight truncate">
                        {{ currentOption?.title ?? "Area of Study" }}
                    </span>
                    <span class="text-xs text-gray-500 leading-tight truncate">
                        {{ currentOption?.description }}
                    </span>
                </div>
                <ChevronUpIcon v-if="open" class="h-4 w-4 text-gray-500 shrink-0" />
                <ChevronDownIcon v-else class="h-4 w-4 text-gray-500 shrink-0" />
            </MenuButton>

            <MenuItems
                class="absolute left-0 top-full w-full  sm:w-[420px] bg-white shadow-xl ring-1 ring-black/10 focus:outline-none z-50 overflow-hidden"
            >
                <div
                    v-if="availableModalityOptions.length === 0"
                    class="px-4 py-6 text-sm text-gray-500 text-center"
                >
                    No courses found for your account.
                </div>

                <template v-else>
                    <MenuItem
                        v-for="option in availableModalityOptions"
                        :key="option.value"
                        v-slot="{ active }"
                    >
                        <button
                            type="button"
                            :class="[
                                'flex w-full items-center gap-3 px-5 py-2 sm:py-5 text-left transition-colors border-b last:border-b-0',
                                option.value === value
                                    ? 'bg-blue-600 text-white'
                                    : active
                                      ? 'bg-blue-50 text-gray-900'
                                      : 'text-gray-800',
                            ]"
                            @click="handleSelect(option.value)"
                        >
                          <svg-icon
                              :icon="option.icon"
                              size="xl"
                              type="fill"
                              :class="['text-gray-900 shrink-0', option.value === value ? 'text-white': '']"
                          ></svg-icon>
                            <div class="flex flex-col min-w-0 flex-1">
                                <span class="font-bold text-sm leading-tight">
                                    {{ option.title }}
                                </span>
                                <span
                                    :class="[
                                        'text-xs leading-tight',
                                        option.value === value
                                            ? 'text-blue-100'
                                            : 'text-gray-500',
                                    ]"
                                >
                                    {{ option.description }}
                                </span>
                            </div>
                            <CheckIcon
                                v-if="option.value === value"
                                class="ml-auto h-5 w-5 shrink-0 text-white"
                            />
                        </button>
                    </MenuItem>
                </template>
            </MenuItems>
        </Menu>

        <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0 translate-y-1"
        >
            <div
                v-if="coachmarkVisible"
                class="absolute left-4 top-full mt-3 z-50 w-80 rounded-md bg-slate-900 p-5 shadow-xl text-white"
            >
                <div class="absolute -top-2 left-8 h-4 w-4 rotate-45 bg-slate-900 rounded-sm" />
                <p class="font-bold text-lg leading-snug mb-2">
                    New Global Filter!
                </p>
                <p class="text-sm text-slate-300 leading-relaxed mb-4">
                    Meet your new shortcut to the right content. Use the Global
                    Filter to instantly narrow down courses and search results
                    &mdash; all from one spot.
                </p>
                <button
                    type="button"
                    class="w-full rounded-md bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
                    @click="dismiss('got_it')"
                >
                    Got it
                </button>
            </div>
        </Transition>
    </div>
</template>
